import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';

import { LoadingStatus } from '@mp/shared/domain';

import { FavoritesList } from '../models';

import { FavoritesActions } from './favorites.actions';

export const favoritesFeatureKey = 'mpcm-favorites';

type FavoritesEntityState = EntityState<FavoritesList>;

export interface FavoritesListState {
  favoritesLists: FavoritesEntityState;
  favoritesListsStatus: LoadingStatus;
}

export const favoritesEntityAdapter = createEntityAdapter<FavoritesList>({
  selectId: (favoritesList: FavoritesList) => favoritesList.id,
});

export const initialState: FavoritesListState = {
  favoritesLists: favoritesEntityAdapter.getInitialState(),
  favoritesListsStatus: LoadingStatus.Loaded,
};

export const favoritesListReducer = createReducer(
  initialState,
  on(
    FavoritesActions.fetchFavoritesLists,
    (state: FavoritesListState): FavoritesListState => ({
      ...state,
      favoritesLists: favoritesEntityAdapter.getInitialState(),
      favoritesListsStatus: LoadingStatus.Loading,
    }),
  ),

  on(
    FavoritesActions.fetchFavoritesListsSuccess,
    (state: FavoritesListState, { favoritesLists }): FavoritesListState => ({
      ...state,
      favoritesLists: favoritesEntityAdapter.setAll(favoritesLists, state.favoritesLists),
      favoritesListsStatus: LoadingStatus.Loaded,
    }),
  ),

  on(
    FavoritesActions.fetchFavoritesListsError,
    (state: FavoritesListState): FavoritesListState => ({
      ...state,
      favoritesLists: favoritesEntityAdapter.getInitialState(),
    }),
  ),

  on(
    FavoritesActions.createFavoritesListSuccess,
    (state: FavoritesListState, { createdList }): FavoritesListState => ({
      ...state,
      favoritesLists: favoritesEntityAdapter.addOne(createdList, state.favoritesLists),
    }),
  ),

  on(
    FavoritesActions.updateFavoritesListSuccess,
    (state: FavoritesListState, { updatedList }): FavoritesListState => ({
      ...state,
      favoritesLists: favoritesEntityAdapter.updateOne(
        { id: updatedList.id, changes: updatedList },
        state.favoritesLists,
      ),
    }),
  ),

  on(
    FavoritesActions.updateFavoritesListArticleCount,
    (state: FavoritesListState, { listId, articleCount }): FavoritesListState => ({
      ...state,
      favoritesLists: favoritesEntityAdapter.updateOne({ id: listId, changes: { articleCount } }, state.favoritesLists),
    }),
  ),

  on(
    FavoritesActions.deleteFavoritesListSuccess,
    (state: FavoritesListState, { listId }): FavoritesListState => ({
      ...state,
      favoritesLists: favoritesEntityAdapter.removeOne(listId, state.favoritesLists),
    }),
  ),

  on(
    FavoritesActions.fetchFavoritesListsError,
    FavoritesActions.createFavoritesListError,
    FavoritesActions.updateFavoritesListError,
    FavoritesActions.deleteFavoritesListError,
    (state: FavoritesListState): FavoritesListState => ({
      ...state,
      favoritesListsStatus: LoadingStatus.Error,
    }),
  ),
);

export const { selectAll: selectAllFavoritesLists } = favoritesEntityAdapter.getSelectors();
